// ===========================================
// BUSTED! BLACKJACK - BETA SURVEY PAGE
// ===========================================
// No-index page for beta tester feedback
// ===========================================

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useFeedbackStore } from '../stores/feedbackStore';
import { APP_VERSION } from '../version';

// ===========================================
// TYPES
// ===========================================

interface SurveyResponses {
  // First Impressions
  q1_clarity: number;
  q2_bet_understanding: string;
  q3_first_impression: string;
  // Gameplay
  q4_pace: string;
  q5_card_animations: number;
  q6_chip_animations: number;
  q7_controls_intuitive: number;
  q8_confusing_rules: string;
  // Audio & Visual
  q9_sound_effects: string;
  q10_table_design: number;
  q11_text_readable: string;
  // BUSTED! Buddy
  q12_used_hints: string;
  q13_hints_helpful: number;
  q14_subscribe_likelihood: number;
  q15_value_24h: number;
  q15_value_30d: number;
  q15_value_1yr: number;
  q16_buddy_features: string[];
  q16_buddy_features_other: string;
  // Performance
  q17_devices: string[];
  q17_devices_other: string;
  q18_browsers: string[];
  q18_browsers_other: string;
  q19_performance: string;
  q20_broken_animations: string;
  // Economy
  q21_starting_balance: string;
  q22_daily_chips: string;
  q23_purchase_chips: string;
  // Social
  q24_multiplayer_importance: string;
  q25_invite_friends: string;
  q26_social_features: string;
  // Overall
  q27_nps: number;
  q28_fix_first: string;
  q29_play_more: string;
  q30_other_feedback: string;
}

const INITIAL_RESPONSES: SurveyResponses = {
  q1_clarity: 0,
  q2_bet_understanding: '',
  q3_first_impression: '',
  q4_pace: '',
  q5_card_animations: 0,
  q6_chip_animations: 0,
  q7_controls_intuitive: 0,
  q8_confusing_rules: '',
  q9_sound_effects: '',
  q10_table_design: 0,
  q11_text_readable: '',
  q12_used_hints: '',
  q13_hints_helpful: 0,
  q14_subscribe_likelihood: 0,
  q15_value_24h: 0,
  q15_value_30d: 0,
  q15_value_1yr: 0,
  q16_buddy_features: [],
  q16_buddy_features_other: '',
  q17_devices: [],
  q17_devices_other: '',
  q18_browsers: [],
  q18_browsers_other: '',
  q19_performance: '',
  q20_broken_animations: '',
  q21_starting_balance: '',
  q22_daily_chips: '',
  q23_purchase_chips: '',
  q24_multiplayer_importance: '',
  q25_invite_friends: '',
  q26_social_features: '',
  q27_nps: 0,
  q28_fix_first: '',
  q29_play_more: '',
  q30_other_feedback: '',
};

// ===========================================
// COMPONENTS
// ===========================================

function ScaleSelector({ value, onChange, max = 5, labels }: {
  value: number;
  onChange: (v: number) => void;
  max?: number;
  labels?: { low: string; high: string };
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 justify-center">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-10 h-10 rounded-lg font-bold transition-all ${
              value === n
                ? 'bg-casino-gold-500 text-black scale-110'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {labels && (
        <div className="flex justify-between text-xs text-gray-500 px-2">
          <span>{labels.low}</span>
          <span>{labels.high}</span>
        </div>
      )}
    </div>
  );
}

function RadioGroup({ options, value, onChange }: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            value === opt.value
              ? 'bg-casino-gold-500 text-black'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function CheckboxGroup({ options, values, onChange }: {
  options: { value: string; label: string }[];
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter(v => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => toggle(opt.value)}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            values.includes(opt.value)
              ? 'bg-casino-gold-500 text-black'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function TextArea({ value, onChange, placeholder, rows = 3 }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-casino-gold-500 resize-none"
    />
  );
}

function QuestionCard({ number, title, children, required = false }: {
  number: number;
  title: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex-shrink-0 w-8 h-8 bg-casino-gold-500 text-black font-bold rounded-full flex items-center justify-center text-sm">
          {number}
        </span>
        <h3 className="text-lg font-medium text-white">
          {title}
          {required && <span className="text-red-400 ml-1">*</span>}
        </h3>
      </div>
      <div className="ml-11">
        {children}
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="border-b border-gray-700 pb-2 mb-6">
      <h2 className="text-xl font-bold text-casino-gold-400">{title}</h2>
    </div>
  );
}

// ===========================================
// BROWSER METADATA COLLECTION
// ===========================================

interface BrowserMetadata {
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  timezone: string;
  language: string;
  languages: string[];
  isTouchDevice: boolean;
  colorDepth: number;
  hardwareConcurrency: number | null;
  deviceMemory: number | null;
  connectionType: string | null;
  referrer: string;
  cookiesEnabled: boolean;
}

function collectBrowserMetadata(): BrowserMetadata {
  // Connection type (experimental API)
  const connection = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;

  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    languages: [...navigator.languages],
    isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    colorDepth: window.screen.colorDepth,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    deviceMemory: (navigator as unknown as { deviceMemory?: number }).deviceMemory || null,
    connectionType: connection?.effectiveType || null,
    referrer: document.referrer,
    cookiesEnabled: navigator.cookieEnabled,
  };
}

// ===========================================
// MAIN PAGE
// ===========================================

export function SurveyPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { openFeedback } = useFeedbackStore();
  const [responses, setResponses] = useState<SurveyResponses>(INITIAL_RESPONSES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rewardAmount, setRewardAmount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  // Check if user has already completed the survey
  useEffect(() => {
    async function checkSurveyStatus() {
      if (!isAuthenticated || authLoading) {
        setIsCheckingStatus(false);
        return;
      }

      try {
        const result = await api.survey.status('beta-v1');
        if (result.success && result.data?.completed) {
          // User already completed - redirect to lobby with feedback modal open
          openFeedback();
          navigate('/', { replace: true });
          return;
        }
      } catch (err) {
        // If status check fails, allow them to continue (submit will catch duplicates)
        console.error('[Survey] Status check failed:', err);
      }

      setIsCheckingStatus(false);
    }

    checkSurveyStatus();
  }, [isAuthenticated, authLoading, navigate, openFeedback]);

  // Add noindex meta tag
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const updateResponse = <K extends keyof SurveyResponses>(key: K, value: SurveyResponses[K]) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isAuthenticated) {
      setError('Please log in to submit the survey');
      return;
    }

    setIsSubmitting(true);

    try {
      // Collect browser metadata
      const browserMetadata = collectBrowserMetadata();

      // Email is no longer required - backend uses authenticated user
      const result = await api.survey.submit(
        '',
        responses as unknown as Record<string, unknown>,
        'beta-v1',
        browserMetadata as unknown as Record<string, unknown>
      );

      if (!result.success) {
        setError(result.error?.message || 'Failed to submit survey. Please try again.');
        return;
      }

      setRewardAmount(result.data?.rewardGranted || 0);
      setSubmitted(true);
    } catch (err: unknown) {
      const apiErr = err as { error?: { message?: string } };
      setError(apiErr.error?.message || 'Failed to submit survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking auth or survey status
  if (authLoading || isCheckingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-casino-gold-400"></div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-6">🔐</div>
          <h1 className="text-3xl font-bold text-casino-gold-400 mb-4">Login Required</h1>
          <p className="text-gray-300 mb-8">
            Please log in to complete the beta tester survey. This helps us track your feedback
            alongside your gameplay experience.
          </p>
          <button
            onClick={() => navigate('/login', { state: { from: { pathname: '/survey' } } })}
            className="inline-block px-6 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-black font-bold rounded-lg transition-colors"
          >
            Log In to Continue
          </button>
          <p className="text-gray-500 text-sm mt-4">
            <Link to="/" className="hover:text-gray-300">← Back to Home</Link>
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-6">🎉</div>
          <h1 className="text-3xl font-bold text-casino-gold-400 mb-4">Thank You!</h1>
          <p className="text-gray-300 mb-4">
            Your feedback has been submitted successfully. We truly appreciate your time and input
            in making BUSTED! Blackjack better.
          </p>
          {rewardAmount > 0 && (
            <div className="bg-casino-gold-500/20 border border-casino-gold-500/50 rounded-xl p-4 mb-8">
              <p className="text-casino-gold-400 font-bold text-lg">
                +{rewardAmount.toLocaleString()} B-Bucks added to your balance!
              </p>
            </div>
          )}
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-black font-bold rounded-lg transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Header */}
      <header className="bg-gray-900/80 border-b border-casino-gold-500/30 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <span className="text-2xl font-bold text-casino-gold-400">BUSTED!</span>
            <span className="text-gray-400 text-sm">Blackjack</span>
          </Link>
          <span className="text-gray-500 text-sm">Beta Survey</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-casino-gold-400 mb-4">
            Beta Tester Survey
          </h1>
          <p className="text-gray-400">
            Help us improve BUSTED! Blackjack. Your feedback shapes the future of the game.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Logged In User */}
          <div className="bg-gray-800 border border-casino-gold-500/50 rounded-xl p-6">
            <div className="flex items-center gap-4">
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.username}
                  className="w-12 h-12 rounded-full"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-casino-gold-500 flex items-center justify-center text-gray-900 font-bold text-lg">
                  {user?.username?.charAt(0).toUpperCase() || '?'}
                </div>
              )}
              <div>
                <p className="text-lg font-medium text-white">
                  {user?.username}
                  {user?.discriminator && <span className="text-gray-500">#{user.discriminator}</span>}
                </p>
                <p className="text-sm text-gray-400">
                  Logged in via {user?.provider || 'unknown'} • Your response will be linked to your account
                </p>
              </div>
            </div>
          </div>

          {/* First Impressions */}
          <SectionHeader title="First Impressions" />

          <QuestionCard number={1} title="How clear was it what to do when you first saw the game table?">
            <ScaleSelector
              value={responses.q1_clarity}
              onChange={(v) => updateResponse('q1_clarity', v)}
              labels={{ low: 'Very confusing', high: 'Crystal clear' }}
            />
          </QuestionCard>

          <QuestionCard number={2} title="Did you understand how to place a bet on your first try?">
            <RadioGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'few_attempts', label: 'Took a few attempts' },
              ]}
              value={responses.q2_bet_understanding}
              onChange={(v) => updateResponse('q2_bet_understanding', v)}
            />
          </QuestionCard>

          <QuestionCard number={3} title="What was your first impression of the game's visual design?">
            <TextArea
              value={responses.q3_first_impression}
              onChange={(v) => updateResponse('q3_first_impression', v)}
              placeholder="Share your thoughts..."
            />
          </QuestionCard>

          {/* Gameplay Experience */}
          <SectionHeader title="Gameplay Experience" />

          <QuestionCard number={4} title="How would you rate the pace of the game?">
            <RadioGroup
              options={[
                { value: 'too_slow', label: 'Too slow' },
                { value: 'just_right', label: 'Just right' },
                { value: 'too_fast', label: 'Too fast' },
              ]}
              value={responses.q4_pace}
              onChange={(v) => updateResponse('q4_pace', v)}
            />
          </QuestionCard>

          <QuestionCard number={5} title="Are the card animations smooth and satisfying?">
            <ScaleSelector
              value={responses.q5_card_animations}
              onChange={(v) => updateResponse('q5_card_animations', v)}
              labels={{ low: 'Poor', high: 'Excellent' }}
            />
          </QuestionCard>

          <QuestionCard number={6} title="Are the chip animations smooth and satisfying?">
            <ScaleSelector
              value={responses.q6_chip_animations}
              onChange={(v) => updateResponse('q6_chip_animations', v)}
              labels={{ low: 'Poor', high: 'Excellent' }}
            />
          </QuestionCard>

          <QuestionCard number={7} title="How intuitive are the game controls (Hit, Stand, Double, Split, Surrender)?">
            <ScaleSelector
              value={responses.q7_controls_intuitive}
              onChange={(v) => updateResponse('q7_controls_intuitive', v)}
              labels={{ low: 'Confusing', high: 'Very intuitive' }}
            />
          </QuestionCard>

          <QuestionCard number={8} title="Did you encounter any confusing rules or unexpected outcomes?">
            <TextArea
              value={responses.q8_confusing_rules}
              onChange={(v) => updateResponse('q8_confusing_rules', v)}
              placeholder="Describe any confusion or leave blank if none..."
            />
          </QuestionCard>

          {/* Audio & Visual */}
          <SectionHeader title="Audio & Visual" />

          <QuestionCard number={9} title="How do you feel about the sound effects?">
            <RadioGroup
              options={[
                { value: 'love', label: 'Love them' },
                { value: 'fine', label: "They're fine" },
                { value: 'too_loud', label: 'Too loud/annoying' },
                { value: 'muted', label: 'I muted them' },
              ]}
              value={responses.q9_sound_effects}
              onChange={(v) => updateResponse('q9_sound_effects', v)}
            />
          </QuestionCard>

          <QuestionCard number={10} title="Is the table design visually appealing?">
            <ScaleSelector
              value={responses.q10_table_design}
              onChange={(v) => updateResponse('q10_table_design', v)}
              labels={{ low: 'Not appealing', high: 'Beautiful' }}
            />
          </QuestionCard>

          <QuestionCard number={11} title="Is the text easy to read throughout the game?">
            <RadioGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'mostly', label: 'Mostly' },
                { value: 'no', label: 'No' },
              ]}
              value={responses.q11_text_readable}
              onChange={(v) => updateResponse('q11_text_readable', v)}
            />
          </QuestionCard>

          {/* BUSTED! Buddy */}
          <SectionHeader title="BUSTED! Buddy (Strategy Helper)" />

          <QuestionCard number={12} title="Did you use the Play Hints feature?">
            <RadioGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'didnt_know', label: "Didn't know it existed" },
              ]}
              value={responses.q12_used_hints}
              onChange={(v) => updateResponse('q12_used_hints', v)}
            />
          </QuestionCard>

          <QuestionCard number={13} title="If yes, was the advice helpful and easy to understand?">
            <ScaleSelector
              value={responses.q13_hints_helpful}
              onChange={(v) => updateResponse('q13_hints_helpful', v)}
              labels={{ low: 'Not helpful', high: 'Very helpful' }}
            />
          </QuestionCard>

          <QuestionCard number={14} title="How likely would you be to subscribe to BUSTED! Buddy?">
            <ScaleSelector
              value={responses.q14_subscribe_likelihood}
              onChange={(v) => updateResponse('q14_subscribe_likelihood', v)}
              labels={{ low: 'Definitely not', high: 'Definitely would' }}
            />
          </QuestionCard>

          <QuestionCard number={15} title="Rate each pricing tier on value (1 = Poor value, 5 = Great value):">
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">⚡</span>
                  <span className="text-gray-300">24 Hours @ $1</span>
                </div>
                <ScaleSelector
                  value={responses.q15_value_24h}
                  onChange={(v) => updateResponse('q15_value_24h', v)}
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">💪</span>
                  <span className="text-gray-300">30 Days @ $5</span>
                </div>
                <ScaleSelector
                  value={responses.q15_value_30d}
                  onChange={(v) => updateResponse('q15_value_30d', v)}
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">💎</span>
                  <span className="text-gray-300">1 Year @ $25</span>
                </div>
                <ScaleSelector
                  value={responses.q15_value_1yr}
                  onChange={(v) => updateResponse('q15_value_1yr', v)}
                />
              </div>
            </div>
          </QuestionCard>

          <QuestionCard number={16} title="What features would make BUSTED! Buddy worth paying for?">
            <div className="space-y-3">
              <CheckboxGroup
                options={[
                  { value: 'card_counting', label: 'Card counting hints' },
                  { value: 'bankroll', label: 'Bankroll management advice' },
                  { value: 'session_stats', label: 'Session statistics & analysis' },
                  { value: 'training', label: 'Personalized strategy training' },
                  { value: 'voice', label: 'Voice announcements' },
                ]}
                values={responses.q16_buddy_features}
                onChange={(v) => updateResponse('q16_buddy_features', v)}
              />
              <input
                type="text"
                value={responses.q16_buddy_features_other}
                onChange={(e) => updateResponse('q16_buddy_features_other', e.target.value)}
                placeholder="Other (please specify)"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-casino-gold-500"
              />
            </div>
          </QuestionCard>

          {/* Performance & Technical */}
          <SectionHeader title="Performance & Technical" />

          <QuestionCard number={17} title="What device(s) did you use to play?">
            <div className="space-y-3">
              <CheckboxGroup
                options={[
                  { value: 'windows', label: 'Windows' },
                  { value: 'macos', label: 'macOS' },
                  { value: 'linux', label: 'Linux' },
                  { value: 'chromeos', label: 'ChromeOS' },
                  { value: 'iphone', label: 'iPhone' },
                  { value: 'ipad', label: 'iPad' },
                  { value: 'android_phone', label: 'Android Phone' },
                  { value: 'android_tablet', label: 'Android Tablet' },
                ]}
                values={responses.q17_devices}
                onChange={(v) => updateResponse('q17_devices', v)}
              />
              <input
                type="text"
                value={responses.q17_devices_other}
                onChange={(e) => updateResponse('q17_devices_other', e.target.value)}
                placeholder="Other (please specify)"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-casino-gold-500"
              />
            </div>
          </QuestionCard>

          <QuestionCard number={18} title="What browser(s) did you use?">
            <div className="space-y-3">
              <CheckboxGroup
                options={[
                  { value: 'chrome', label: 'Chrome' },
                  { value: 'firefox', label: 'Firefox' },
                  { value: 'safari', label: 'Safari' },
                  { value: 'edge', label: 'Edge' },
                  { value: 'brave', label: 'Brave' },
                ]}
                values={responses.q18_browsers}
                onChange={(v) => updateResponse('q18_browsers', v)}
              />
              <input
                type="text"
                value={responses.q18_browsers_other}
                onChange={(e) => updateResponse('q18_browsers_other', e.target.value)}
                placeholder="Other (please specify)"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-casino-gold-500"
              />
            </div>
          </QuestionCard>

          <QuestionCard number={19} title="Did you experience any lag, freezing, or crashes?">
            <RadioGroup
              options={[
                { value: 'never', label: 'Never' },
                { value: 'occasionally', label: 'Occasionally' },
                { value: 'frequently', label: 'Frequently' },
              ]}
              value={responses.q19_performance}
              onChange={(v) => updateResponse('q19_performance', v)}
            />
          </QuestionCard>

          <QuestionCard number={20} title="Did any animations appear broken or out of place?">
            <TextArea
              value={responses.q20_broken_animations}
              onChange={(v) => updateResponse('q20_broken_animations', v)}
              placeholder="Describe any issues or leave blank if none..."
            />
          </QuestionCard>

          {/* Economy & Progression */}
          <SectionHeader title="Economy & Progression" />

          <QuestionCard number={21} title="Does the starting chip balance feel appropriate?">
            <RadioGroup
              options={[
                { value: 'too_low', label: 'Too low' },
                { value: 'just_right', label: 'Just right' },
                { value: 'too_high', label: 'Too high' },
              ]}
              value={responses.q21_starting_balance}
              onChange={(v) => updateResponse('q21_starting_balance', v)}
            />
          </QuestionCard>

          <QuestionCard number={22} title="How do you feel about the daily free chips amount?">
            <RadioGroup
              options={[
                { value: 'want_more', label: 'Want more' },
                { value: 'fair', label: "It's fair" },
                { value: 'dont_care', label: "Don't care" },
              ]}
              value={responses.q22_daily_chips}
              onChange={(v) => updateResponse('q22_daily_chips', v)}
            />
          </QuestionCard>

          <QuestionCard number={23} title="Would you consider purchasing chips to keep playing?">
            <RadioGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'maybe', label: 'Maybe' },
                { value: 'no', label: 'No' },
              ]}
              value={responses.q23_purchase_chips}
              onChange={(v) => updateResponse('q23_purchase_chips', v)}
            />
          </QuestionCard>

          {/* Social Features */}
          <SectionHeader title="Social Features" />

          <QuestionCard number={24} title="How important is multiplayer to you?">
            <RadioGroup
              options={[
                { value: 'essential', label: 'Essential' },
                { value: 'nice_to_have', label: 'Nice to have' },
                { value: 'dont_care', label: "Don't care" },
              ]}
              value={responses.q24_multiplayer_importance}
              onChange={(v) => updateResponse('q24_multiplayer_importance', v)}
            />
          </QuestionCard>

          <QuestionCard number={25} title="Would you invite friends to play at your personal table?">
            <RadioGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'maybe', label: 'Maybe' },
                { value: 'no', label: 'No' },
              ]}
              value={responses.q25_invite_friends}
              onChange={(v) => updateResponse('q25_invite_friends', v)}
            />
          </QuestionCard>

          <QuestionCard number={26} title="What social features would you like to see added?">
            <TextArea
              value={responses.q26_social_features}
              onChange={(v) => updateResponse('q26_social_features', v)}
              placeholder="Share your ideas..."
            />
          </QuestionCard>

          {/* Overall */}
          <SectionHeader title="Overall" />

          <QuestionCard number={27} title="How likely are you to recommend BUSTED! Blackjack to a friend?">
            <ScaleSelector
              value={responses.q27_nps}
              onChange={(v) => updateResponse('q27_nps', v)}
              max={10}
              labels={{ low: 'Not at all likely', high: 'Extremely likely' }}
            />
          </QuestionCard>

          <QuestionCard number={28} title="What's the ONE thing we should fix or improve first?">
            <TextArea
              value={responses.q28_fix_first}
              onChange={(v) => updateResponse('q28_fix_first', v)}
              placeholder="The most important thing..."
            />
          </QuestionCard>

          <QuestionCard number={29} title="What feature would make you play more often?">
            <TextArea
              value={responses.q29_play_more}
              onChange={(v) => updateResponse('q29_play_more', v)}
              placeholder="What would bring you back..."
            />
          </QuestionCard>

          <QuestionCard number={30} title="Any other feedback?">
            <TextArea
              value={responses.q30_other_feedback}
              onChange={(v) => updateResponse('q30_other_feedback', v)}
              placeholder="Anything else you'd like to share..."
              rows={5}
            />
          </QuestionCard>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-casino-gold-500 hover:bg-casino-gold-400 text-black font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Survey'}
            </button>
          </div>
        </form>

        {/* Footer */}
        <footer className="mt-12 text-center text-gray-500 text-sm">
          <p>BUSTED! Blackjack v{APP_VERSION}</p>
          <p className="mt-1">Thank you for helping us improve the game!</p>
        </footer>
      </main>
    </div>
  );
}

export default SurveyPage;
